/**
 * Button Component for Puck Editor
 * 
 * Tombol CTA dengan berbagai style
 */

import type { ComponentConfig } from '@measured/puck'

export interface ButtonProps {
  text: string
  url: string
  variant: 'primary' | 'secondary' | 'outline' | 'ghost'
  size: 'small' | 'medium' | 'large'
  fullWidth: boolean
  alignment: 'left' | 'center' | 'right'
  openInNewTab: boolean
}

const variantClasses = {
  primary: 'bg-primary-500 hover:bg-primary-600 text-white',
  secondary: 'bg-gray-600 hover:bg-gray-700 text-white',
  outline: 'border-2 border-primary-500 text-primary-500 hover:bg-primary-50',
  ghost: 'text-primary-500 hover:bg-primary-50',
}

const sizeClasses = {
  small: 'px-4 py-2 text-sm',
  medium: 'px-6 py-3 text-base',
  large: 'px-8 py-4 text-lg',
}

const alignmentClasses = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
}

// Validate URL to prevent javascript: or data: injection attacks
const isSafeUrl = (url: string): boolean => {
  if (!url || url === '#') return true
  try {
    // Handle SSR/SSG where window is not available
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    const parsed = new URL(url, base)
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol)
  } catch {
    return false
  }
}

export function Button({
  text,
  url,
  variant,
  size,
  fullWidth,
  alignment,
  openInNewTab,
}: ButtonProps) {
  // Determine if we should render as link or button for accessibility
  const isValidLink = url && url !== '#' && isSafeUrl(url)
  const safeUrl = isValidLink ? url : '#'
  
  const buttonClasses = `
    inline-flex items-center justify-center
    ${variantClasses[variant]}
    ${sizeClasses[size]}
    ${fullWidth ? 'w-full' : ''}
    rounded-lg font-semibold transition-colors cursor-pointer
  `

  // When fullWidth is true, alignment doesn't apply visually
  const containerClasses = `flex ${fullWidth ? '' : alignmentClasses[alignment]}`

  // Render as button when no valid URL for better accessibility
  if (!isValidLink) {
    return (
      <div className={containerClasses}>
        <button
          type="button"
          className={buttonClasses}
          aria-disabled="true"
          style={{ opacity: 0.6, cursor: 'not-allowed' }}
          title="URL tidak valid atau belum diisi"
        >
          {text}
        </button>
      </div>
    )
  }

  return (
    <div className={containerClasses}>
      <a
        href={safeUrl}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        className={buttonClasses}
      >
        {text}
      </a>
    </div>
  )
}

export const ButtonConfig: ComponentConfig<ButtonProps> = {
  label: 'Tombol',
  defaultProps: {
    text: 'Klik Di Sini',
    url: '#',
    variant: 'primary',
    size: 'medium',
    fullWidth: false,
    alignment: 'left',
    openInNewTab: false,
  },
  fields: {
    text: {
      type: 'text',
      label: 'Teks',
    },
    url: {
      type: 'text',
      label: 'Link',
    },
    variant: {
      type: 'select',
      label: 'Gaya',
      options: [
        { label: 'Primary', value: 'primary' },
        { label: 'Secondary', value: 'secondary' },
        { label: 'Outline', value: 'outline' },
        { label: 'Ghost', value: 'ghost' },
      ],
    },
    size: {
      type: 'select',
      label: 'Ukuran',
      options: [
        { label: 'Kecil', value: 'small' },
        { label: 'Sedang', value: 'medium' },
        { label: 'Besar', value: 'large' },
      ],
    },
    fullWidth: {
      type: 'radio',
      label: 'Lebar Penuh',
      options: [
        { label: 'Ya', value: true },
        { label: 'Tidak', value: false },
      ],
    },
    alignment: {
      type: 'radio',
      label: 'Posisi',
      options: [
        { label: 'Kiri', value: 'left' },
        { label: 'Tengah', value: 'center' },
        { label: 'Kanan', value: 'right' },
      ],
    },
    openInNewTab: {
      type: 'radio',
      label: 'Buka di Tab Baru',
      options: [
        { label: 'Ya', value: true },
        { label: 'Tidak', value: false },
      ],
    },
  },
  render: Button,
}
